import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import util from "../../utils/miniUtils";
import { showApiError } from "./salesHelpers";

// Largos de orders.shipping_carrier y orders.tracking_number en inventory-api
const CARRIER_MAX_LENGTH = 50;
const TRACKING_NUMBER_MAX_LENGTH = 100;

// Marcar Enviado (isCorrection = false) o corregir paquetería/guía de un pedido Enviado (isCorrection = true).
// La lista de paqueterías viene de GET /sales/carriers; con la opción "Otra" se escribe el nombre.
export default function ShipOrderForm( { order, isCorrection, onSaved, onCancel } ) {
    const [ carriers, setCarriers ] = useState( [] );
    const [ otherCarrier, setOtherCarrier ] = useState( "" );

    useEffect( () => {
        loadCarriers().then( null );
    }, [] );

    async function loadCarriers() {
        try {
            const request = await util.axios.get( "sales/carriers" );
            const { error, msg, data } = request.data;
            if ( error ) throw msg;
            setCarriers( data.carriers );
            setOtherCarrier( data.otherCarrier );
        } catch ( e ) {
            showApiError( e );
        }
    }

    // Al corregir, una paquetería que no está en la lista se guardó con "Otra"
    const currentCarrier = order.shipping.carrier || "";
    const currentIsInList = carriers.includes( currentCarrier );

    const validationSchema = Yup.object().shape( {
        carrier: Yup.string().required( "La paquetería es obligatoria" ),
        otherCarrier: Yup.string()
            .max( CARRIER_MAX_LENGTH, `Máximo ${CARRIER_MAX_LENGTH} caracteres` )
            .test( "other-carrier-name", "Escribe el nombre de la paquetería", function ( value ) {
                const choseOther = Boolean( otherCarrier ) && this.parent.carrier === otherCarrier;
                return !choseOther || Boolean( value && value.trim() );
            } ),
        trackingNumber: Yup.string()
            .trim()
            .required( "El número de guía es obligatorio" )
            .max( TRACKING_NUMBER_MAX_LENGTH, `Máximo ${TRACKING_NUMBER_MAX_LENGTH} caracteres` ),
    } );

    const formik = useFormik( {
        enableReinitialize: true,
        initialValues: {
            carrier: currentIsInList || !currentCarrier ? currentCarrier : otherCarrier,
            otherCarrier: currentIsInList ? "" : currentCarrier,
            trackingNumber: order.shipping.trackingNumber || "",
        },
        validationSchema,
        onSubmit: saveShipment,
    } );

    // otherCarrier queda vacío hasta que responde /sales/carriers
    const needsOtherName = Boolean( otherCarrier ) && formik.values.carrier === otherCarrier;

    async function saveShipment( values ) {
        const body = {
            carrier: values.carrier,
            otherCarrier: needsOtherName ? values.otherCarrier.trim() : undefined,
            trackingNumber: values.trackingNumber.trim(),
        };
        try {
            const request = isCorrection
                ? await util.axios.put( `sales/orders/${order.uuid}/tracking`, body )
                : await util.axios.post( `sales/orders/${order.uuid}/ship`, body );
            const { error, msg } = request.data;
            if ( error ) throw msg;
            util.toast().success( msg );
            onSaved();
        } catch ( e ) {
            showApiError( e );
        }
    }

    const fieldError = ( name ) => ( formik.touched[name] || formik.submitCount > 0 ) && formik.errors[name];

    return (
        <form className="sales-ship-form" onSubmit={formik.handleSubmit} data-cy="sales-ship-form">
            <div className="row g-2">
                <div className="col-md-4">
                    <label htmlFor="sales-carrier" className="form-label">Paquetería</label>
                    <select
                        id="sales-carrier"
                        name="carrier"
                        className="form-select form-select-sm"
                        value={formik.values.carrier}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        data-cy="sales-ship-carrier"
                    >
                        <option value="">Selecciona…</option>
                        {carriers.map( ( carrier ) => <option key={carrier} value={carrier}>{carrier}</option> )}
                    </select>
                    {fieldError( "carrier" ) && <div className="text-danger small">{formik.errors.carrier}</div>}
                </div>
                {needsOtherName && (
                    <div className="col-md-4">
                        <label htmlFor="sales-other-carrier" className="form-label">Nombre de la paquetería</label>
                        <input
                            id="sales-other-carrier"
                            name="otherCarrier"
                            className="form-control form-control-sm"
                            value={formik.values.otherCarrier}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            data-cy="sales-ship-other-carrier"
                        />
                        {fieldError( "otherCarrier" ) && <div className="text-danger small">{formik.errors.otherCarrier}</div>}
                    </div>
                )}
                <div className="col-md-4">
                    <label htmlFor="sales-tracking" className="form-label">Número de guía</label>
                    <input
                        id="sales-tracking"
                        name="trackingNumber"
                        className="form-control form-control-sm"
                        value={formik.values.trackingNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        data-cy="sales-ship-tracking"
                    />
                    {fieldError( "trackingNumber" ) && <div className="text-danger small">{formik.errors.trackingNumber}</div>}
                </div>
            </div>
            <div className="mt-2 d-flex gap-2">
                <button type="submit" className="btn btn-sm btn-primary" disabled={formik.isSubmitting} data-cy="sales-ship-submit">
                    {isCorrection ? "Guardar guía" : "Marcar Enviado"}
                </button>
                <button type="button" className="btn btn-sm btn-secondary" onClick={onCancel} data-cy="sales-ship-cancel">Cancelar</button>
            </div>
        </form>
    );
}
